// src/components/staff/tournaments/bracketLayout.ts
//
// Pure bracket layout engine. No React, no DOM, no dependencies.
// Given the games in one bracket, it returns where every matchup sits and
// every connector segment to draw. This is the part that kept breaking, so it
// is isolated here where it can be unit tested without rendering anything.
//
// Sizes come from measurements taken on Tourney Machine's own DOM:
//   team row        27px tall, name sits on the border
//   row centres     75px apart
//   matchup unit    102px tall  (27 + 75)
// A matchup NEVER changes height. Depth is carried by the connectors.

// ---------------------------------------------------------------- constants

export const ROW_H = 27;          // one team row
export const ROW_PITCH = 75;      // distance between the two row centres
export const MATCHUP_H = ROW_H + ROW_PITCH;   // 102
export const MIN_MATCHUP_GAP = 30;            // visible air between two matchups
export const SEED_PITCH = MATCHUP_H + MIN_MATCHUP_GAP; // 132

const COL_MIN = 150;
const COL_MAX = 260;
const GAP_MIN = 40;
const GAP_MAX = 90;
const PAD_TOP = 8;
const PAD_BOTTOM = 8;
const PAD_LEFT = 0;

const clamp = (lo: number, v: number, hi: number) => Math.max(lo, Math.min(v, hi));

// ---------------------------------------------------------------- types

export interface BracketGameInput {
  id: string;
  gameNumber: number;
  /** scheduled_games.bracket_round */
  round: number;
  /**
   * Ids of the games that feed this one, in display order (upper slot first).
   * Empty for games whose slots hold teams or are still unwired.
   * Build with feederIdsFromRow() below.
   */
  feederIds: string[];
  /**
   * Top-to-bottom display order, which the caller already knows.
   *
   * This is NOT the game number. On 16u Winners the first round renders as
   * 19, 18, 16, 20, 21, 22, 23, 24 — seeding order, not numeric order. Passing
   * game numbers here puts two quarterfinals on the same centre line and the
   * overlap pass then shoves one of them down, which is wrong rather than
   * merely ugly.
   *
   * Pass the index of each game in the order the bracket already renders it.
   * Omitted, it falls back to gameNumber, which is only correct when the two
   * happen to agree.
   */
  seedIndex?: number;
}

export interface PlacedMatchup {
  id: string;
  gameNumber: number;
  round: number;
  /** column index, 0-based, in render order */
  col: number;
  x: number;
  y: number;
  w: number;
  /** always MATCHUP_H */
  h: number;
  /** vertical centre of the whole matchup */
  cy: number;
  /** centre of the upper team row */
  slotACy: number;
  /** centre of the lower team row */
  slotBCy: number;
  /** true when this game had no positioned feeders and was placed sequentially */
  unwired: boolean;
}

export type Orientation = 'h' | 'v';

export interface Segment {
  /** left edge for 'h', the line's x for 'v' */
  x: number;
  /** the line's y for 'h', top edge for 'v' */
  y: number;
  /** pixels along the orientation; never negative */
  length: number;
  orientation: Orientation;
  /** the game this segment feeds into, for keys and debugging */
  toGameId: string;
  /**
   * True when this feed skips at least one column, so the segment is longer
   * than a single gap and will cross other columns. Not a layout fault — it
   * means the data has a game fed from something further back than the round
   * before it, like 16u Game 32, a round 5 game fed from two round 1 games.
   * Style these differently or surface them; do not try to shorten them.
   */
  spansColumns: boolean;
}

export interface BracketLayout {
  matchups: PlacedMatchup[];
  connectors: Segment[];
  /** columns in render order, with the round number each one represents */
  columns: { col: number; round: number; x: number; w: number }[];
  /** exact size of the laid-out content — use this for the canvas box */
  width: number;
  height: number;
  colW: number;
  gap: number;
}

export interface LayoutOptions {
  /** measured width of the scroll pane. Column size is derived from it. */
  containerWidth: number;
}

// ---------------------------------------------------------------- helpers

/**
 * Pull feeder game ids out of a scheduled_games row.
 * source_a_ref / source_b_ref are jsonb and hold {"game_id": "..."} when the
 * slot is fed by another game, or {"registration_id": "..."} when it holds a
 * team. bracket_feed_a / bracket_feed_b are stale display text — never use them.
 */
export function feederIdsFromRow(row: {
  source_a_type?: string | null;
  source_a_ref?: unknown;
  source_b_type?: string | null;
  source_b_ref?: unknown;
}): string[] {
  const pick = (type: unknown, ref: unknown): string | null => {
    if (type !== 'winner_of' && type !== 'loser_of') return null;
    if (!ref || typeof ref !== 'object') return null;
    const id = (ref as Record<string, unknown>).game_id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  };
  return [pick(row.source_a_type, row.source_a_ref), pick(row.source_b_type, row.source_b_ref)]
    .filter((v): v is string => v !== null);
}

// ---------------------------------------------------------------- layout

export function computeBracketLayout(
  games: BracketGameInput[],
  opts: LayoutOptions,
): BracketLayout {
  if (games.length === 0) {
    return { matchups: [], connectors: [], columns: [], width: 0, height: 0, colW: COL_MIN, gap: GAP_MIN };
  }

  // Columns follow bracket_round, in ascending order. A round with no games
  // does not get a column, so gaps in round numbering do not leave dead space.
  const rounds = [...new Set(games.map(g => g.round))].sort((a, b) => a - b);
  const colOfRound = new Map(rounds.map((r, i) => [r, i]));

  // --- horizontal sizing, derived from the measured container -------------
  const n = rounds.length;
  const gap = clamp(GAP_MIN, Math.round(opts.containerWidth * 0.06), GAP_MAX);
  const fits = Math.floor((opts.containerWidth - PAD_LEFT - gap * (n - 1)) / Math.max(n, 1));
  const colW = clamp(COL_MIN, fits, COL_MAX);
  const xOfCol = (c: number) => PAD_LEFT + c * (colW + gap);

  // --- vertical placement --------------------------------------------------
  const byId = new Map(games.map(g => [g.id, g]));
  const cy = new Map<string, number>();
  const unwired = new Set<string>();

  for (const round of rounds) {
    const order = (g: BracketGameInput) => (g.seedIndex ?? g.gameNumber);
    const inRound = games
      .filter(g => g.round === round)
      .sort((a, b) => order(a) - order(b));

    const computed: { g: BracketGameInput; cy: number }[] = [];
    const pending: BracketGameInput[] = [];

    for (const g of inRound) {
      // Only feeders that exist in THIS bracket and are already placed count.
      const ys = g.feederIds
        .filter(fid => byId.has(fid) && cy.has(fid))
        .map(fid => cy.get(fid)!);
      if (ys.length > 0) {
        computed.push({ g, cy: ys.reduce((a, b) => a + b, 0) / ys.length });
      } else {
        pending.push(g);
      }
    }

    // Games with no usable feeders — the whole first round, and every game in a
    // freshly generated consolation bracket whose slots are still null — are
    // stacked on the seed pitch rather than collapsing onto each other.
    let next = computed.length > 0
      ? Math.max(...computed.map(c => c.cy)) + SEED_PITCH
      : PAD_TOP + MATCHUP_H / 2;
    for (const g of pending) {
      computed.push({ g, cy: next });
      unwired.add(g.id);
      next += SEED_PITCH;
    }

    // Nothing in a column may overlap. Walk top to bottom and push down anything
    // closer than one matchup plus the minimum gap.
    computed.sort((a, b) => a.cy - b.cy);
    let floor = -Infinity;
    for (const c of computed) {
      const y = Math.max(c.cy, floor);
      cy.set(c.g.id, y);
      floor = y + MATCHUP_H + MIN_MATCHUP_GAP;
    }
  }

  // Never let content start above the canvas. Shift everything down if the
  // topmost matchup would have a negative top edge.
  const minTop = Math.min(...games.map(g => cy.get(g.id)! - MATCHUP_H / 2));
  const shift = minTop < PAD_TOP ? PAD_TOP - minTop : 0;

  const matchups: PlacedMatchup[] = games.map(g => {
    const col = colOfRound.get(g.round)!;
    const centre = cy.get(g.id)! + shift;
    return {
      id: g.id,
      gameNumber: g.gameNumber,
      round: g.round,
      col,
      x: xOfCol(col),
      y: centre - MATCHUP_H / 2,
      w: colW,
      h: MATCHUP_H,
      cy: centre,
      slotACy: centre - ROW_PITCH / 2,
      slotBCy: centre + ROW_PITCH / 2,
      unwired: unwired.has(g.id),
    };
  });

  const placed = new Map(matchups.map(m => [m.id, m]));

  // --- connectors ----------------------------------------------------------
  // Thin segments only, never a box. The vertical always sits in the gap
  // immediately to the left of the child, so for a normal feed from the round
  // before, no segment is longer than one gap. A feed that skips columns still
  // bends in that same gap; its horizontal run is long by necessity and is
  // flagged with spansColumns rather than hidden.
  const connectors: Segment[] = [];

  for (const g of games) {
    const child = placed.get(g.id)!;
    const feeders = g.feederIds
      .map(fid => placed.get(fid))
      .filter((m): m is PlacedMatchup => !!m);
    if (feeders.length === 0) continue;

    const midX = Math.round(child.x - gap / 2);
    const childCy = Math.round(child.cy);

    const ys: number[] = [];
    for (const f of feeders) {
      const y = Math.round(f.cy);
      ys.push(y);
      const from = f.x + f.w;
      if (midX > from) {
        connectors.push({
          x: from,
          y,
          length: midX - from,
          orientation: 'h',
          toGameId: g.id,
          spansColumns: child.col - f.col > 1,
        });
      }
    }

    const top = Math.min(...ys, childCy);
    const bottom = Math.max(...ys, childCy);
    if (bottom > top) {
      connectors.push({
        x: midX,
        y: top,
        length: bottom - top,
        orientation: 'v',
        toGameId: g.id,
        spansColumns: false,
      });
    }

    if (child.x > midX) {
      connectors.push({
        x: midX,
        y: childCy,
        length: child.x - midX,
        orientation: 'h',
        toGameId: g.id,
        spansColumns: false,
      });
    }
  }

  // --- exact content box ---------------------------------------------------
  // The canvas height is measured from what was actually laid out. This is the
  // fix for the content overflowing its own container.
  const height = Math.ceil(Math.max(...matchups.map(m => m.y + m.h)) + PAD_BOTTOM);
  const width = Math.ceil(Math.max(...matchups.map(m => m.x + m.w)));

  const columns = rounds.map((round, col) => ({ col, round, x: xOfCol(col), w: colW }));

  return { matchups, connectors, columns, width, height, colW, gap };
}

// ---------------------------------------------------------------- fit to screen

/**
 * Scale that makes the whole bracket visible inside the pane. Never scales up.
 * Apply as `transform: scale(fit)` with `transform-origin: top left` on the
 * canvas, and set the wrapper's size to width*fit / height*fit so the scroll
 * area matches what is drawn.
 */
export function fitToScreen(
  layout: Pick<BracketLayout, 'width' | 'height'>,
  pane: { width: number; height: number },
): number {
  if (!layout.width || !layout.height) return 1;
  return Math.min(1, pane.width / layout.width, pane.height / layout.height);
}
